import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import type { Message } from "../interfaces";
import type { SelfHelpFeedbackMessageDescriptor } from "../interfaces/api";
import type {
  SelfHelpFeedbackCategory,
  SelfHelpFeedbackState,
} from "../interfaces/selfHelpFeedback";
import type { SelfHelpFeedbackJourneyEventDetail } from "./selfHelpFeedbackSession";

export const SELF_HELP_FEEDBACK_TELEMETRY_EVENTS = {
  captured: "SELF_HELP_FEEDBACK_CAPTURED",
  abandoned: "SELF_HELP_FEEDBACK_ABANDONED",
  ticketStarted: "SELF_HELP_FEEDBACK_TICKET_STARTED",
  validatorFailed: "SELF_HELP_FEEDBACK_VALIDATOR_FAILED",
} as const;

export const SELF_HELP_FEEDBACK_TELEMETRY_OUTCOMES = {
  resolved: "Resolved",
  stillNeedsHelp: "StillNeedsHelp",
} as const;

const CATEGORY_WIRE_VALUES: Record<
  SelfHelpFeedbackCategory,
  SelfHelpFeedbackCategory
> = {
  stepsDidNotResolveIssue: "stepsDidNotResolveIssue",
  couldNotCompleteStep: "couldNotCompleteStep",
  instructionsDidNotMatchExperience: "instructionsDidNotMatchExperience",
  receivedErrorMessage: "receivedErrorMessage",
  somethingElse: "somethingElse",
};

export const serializeSelfHelpFeedbackCategory = (
  category: SelfHelpFeedbackCategory
): SelfHelpFeedbackCategory => CATEGORY_WIRE_VALUES[category];

type TelemetryProperties = Record<string, string | boolean>;
type TrackEvent = (event: {
  name: string;
  properties: TelemetryProperties;
}) => void;

interface JourneySnapshot {
  descriptor: SelfHelpFeedbackMessageDescriptor;
  state: SelfHelpFeedbackState;
}

const getJourneySnapshot = (
  messages: Message[],
  journeyId: string,
  selfHelpTurnId: string
): JourneySnapshot | null => {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    const descriptor =
      message.meta?.selfHelpFeedback ||
      message.meta?.dataToDisplay?.selfHelpFeedback;
    const state = message.meta?.selfHelpFeedbackState;
    if (
      descriptor &&
      state &&
      state.journeyId === journeyId &&
      descriptor.selfHelpTurnId === selfHelpTurnId
    ) {
      return { descriptor, state };
    }
  }
  return null;
};

const safeDescriptorProperties = (
  descriptor: SelfHelpFeedbackMessageDescriptor
): TelemetryProperties => {
  const properties: TelemetryProperties = {};
  if (descriptor.serviceName) properties.serviceName = descriptor.serviceName;
  if (descriptor.requestType) properties.requestType = descriptor.requestType;
  if (descriptor.appName) properties.appName = descriptor.appName;
  return properties;
};

const isTerminalState = (state: SelfHelpFeedbackState): boolean =>
  state.status === "resolved" || state.status === "abandoned";

const TELEMETRY_DEDUPE_STORAGE_KEY =
  "mcapsCopilot_selfHelpFeedbackTelemetryDedupe";

type PersistedTelemetryDedupe = {
  completed: string[];
  abandoned: string[];
  started: string[];
};

const readPersistedDedupe = (): PersistedTelemetryDedupe => {
  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(TELEMETRY_DEDUPE_STORAGE_KEY) || "{}"
    );
    return {
      completed: Array.isArray(parsed.completed) ? parsed.completed : [],
      abandoned: Array.isArray(parsed.abandoned) ? parsed.abandoned : [],
      started: Array.isArray(parsed.started) ? parsed.started : [],
    };
  } catch {
    return { completed: [], abandoned: [], started: [] };
  }
};

const persistDedupe = (
  completed: Set<string>,
  abandoned: Set<string>,
  started: Set<string>
): void => {
  try {
    sessionStorage.setItem(
      TELEMETRY_DEDUPE_STORAGE_KEY,
      JSON.stringify({
        completed: Array.from(completed),
        abandoned: Array.from(abandoned),
        started: Array.from(started),
      })
    );
  } catch {
    // Telemetry remains best-effort if browser storage is unavailable.
  }
};

export const createSelfHelpFeedbackTelemetryAdapter = (
  trackEvent: TrackEvent,
  getMessages: () => Message[]
) => {
  const persisted = readPersistedDedupe();
  const completedJourneys = new Set<string>(persisted.completed);
  const abandonedJourneys = new Set<string>(persisted.abandoned);
  const startedJourneys = new Set<string>(persisted.started);

  const handleJourneyEvent = (
    event: SelfHelpFeedbackJourneyEventDetail
  ): void => {
    const dedupeKey = `${event.journeyId}:${event.selfHelpTurnId}`;
    const snapshot = getJourneySnapshot(
      getMessages(),
      event.journeyId,
      event.selfHelpTurnId
    );

    if (event.type === "abandoned") {
      if (
        !snapshot ||
        completedJourneys.has(dedupeKey) ||
        abandonedJourneys.has(dedupeKey) ||
        isTerminalState(snapshot.state)
      ) {
        return;
      }
      abandonedJourneys.add(dedupeKey);
      persistDedupe(completedJourneys, abandonedJourneys, startedJourneys);
      trackEvent({
        name: SELF_HELP_FEEDBACK_TELEMETRY_EVENTS.abandoned,
        properties: {
          abandonmentReason: event.reason,
          ...safeDescriptorProperties(snapshot.descriptor),
        },
      });
      return;
    }

    if (completedJourneys.has(dedupeKey)) {
      return;
    }

    if (event.type === "resolved") {
      if (!snapshot || abandonedJourneys.has(dedupeKey)) {
        return;
      }
      completedJourneys.add(dedupeKey);
      persistDedupe(completedJourneys, abandonedJourneys, startedJourneys);
      trackEvent({
        name: SELF_HELP_FEEDBACK_TELEMETRY_EVENTS.captured,
        properties: {
          outcome: SELF_HELP_FEEDBACK_TELEMETRY_OUTCOMES.resolved,
          feedbackProvided: false,
          validatorFallback: false,
          clarificationUsed: false,
          ticketCreated: false,
          formRendered: false,
          ...safeDescriptorProperties(snapshot.descriptor),
        },
      });
      return;
    }

    const category =
      event.category ??
      (snapshot && "category" in snapshot.state
        ? snapshot.state.category
        : undefined);
    const primaryFailureReason =
      event.primaryFailureReason ??
      (snapshot && "primaryFailureReason" in snapshot.state
        ? snapshot.state.primaryFailureReason
        : undefined);
    const descriptorProperties = snapshot
      ? safeDescriptorProperties(snapshot.descriptor)
      : {};
    const isAuthoritativeTicketEvent =
      event.type === "ticket-form-rendered" ||
      event.type === "ticket-submission-succeeded";

    if (abandonedJourneys.has(dedupeKey) && !isAuthoritativeTicketEvent) {
      return;
    }

    if (!category) {
      return;
    }

    if (event.type === "ticket-form-rendered") {
      abandonedJourneys.delete(dedupeKey);
      if (startedJourneys.has(dedupeKey)) {
        return;
      }
      startedJourneys.add(dedupeKey);
      persistDedupe(completedJourneys, abandonedJourneys, startedJourneys);
      trackEvent({
        name: SELF_HELP_FEEDBACK_TELEMETRY_EVENTS.ticketStarted,
        properties: {
          category: serializeSelfHelpFeedbackCategory(category),
          feedbackProvided: true,
          validatorFallback: event.validatorFallback === true,
          clarificationUsed: event.clarificationUsed === true,
          ...(primaryFailureReason
            ? { primaryFailureReason }
            : {}),
          ...descriptorProperties,
        },
      });
      return;
    }

    if (event.type === "ticket-submission-failed") {
      return;
    }

    if (event.type !== "ticket-submission-succeeded") {
      return;
    }

    abandonedJourneys.delete(dedupeKey);
    completedJourneys.add(dedupeKey);
    persistDedupe(completedJourneys, abandonedJourneys, startedJourneys);
    trackEvent({
      name: SELF_HELP_FEEDBACK_TELEMETRY_EVENTS.captured,
      properties: {
        outcome: SELF_HELP_FEEDBACK_TELEMETRY_OUTCOMES.stillNeedsHelp,
        category: serializeSelfHelpFeedbackCategory(category),
        feedbackProvided: true,
        validatorFallback: event.validatorFallback === true,
        clarificationUsed: event.clarificationUsed === true,
        ...(primaryFailureReason
          ? { primaryFailureReason }
          : {}),
        ticketCreated: true,
        formRendered: true,
        ...descriptorProperties,
      },
    });
  };

  return { handleJourneyEvent };
};

let appInsights: ApplicationInsights | null = null;
const trackApplicationInsightsEvent: TrackEvent = (event) => {
  try {
    if (!appInsights) {
      appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY,
        },
      });
      appInsights.loadAppInsights();
    }
    appInsights.trackEvent(event);
  } catch {
    // Telemetry is best-effort and must never affect the feedback journey.
  }
};

export interface SelfHelpFeedbackValidatorFailure {
  failureType: "transport" | "server";
  validationStatus?: string;
  httpStatus?: number;
  correlationId?: string;
}

export const trackSelfHelpFeedbackValidatorFailure = (
  failure: SelfHelpFeedbackValidatorFailure
): void => {
  const properties: TelemetryProperties = {
    failureType: failure.failureType,
  };
  if (failure.validationStatus) {
    properties.validationStatus = failure.validationStatus;
  }
  if (typeof failure.httpStatus === "number") {
    properties.httpStatus = String(failure.httpStatus);
  }
  if (failure.correlationId) {
    properties.correlationId = failure.correlationId;
  }

  trackApplicationInsightsEvent({
    name: SELF_HELP_FEEDBACK_TELEMETRY_EVENTS.validatorFailed,
    properties,
  });
};

export const attachSelfHelpFeedbackTelemetryListener = (
  getMessages: () => Message[]
): (() => void) => {
  const adapter = createSelfHelpFeedbackTelemetryAdapter(
    trackApplicationInsightsEvent,
    getMessages
  );
  const listener = (event: Event) => {
    adapter.handleJourneyEvent(
      (event as CustomEvent<SelfHelpFeedbackJourneyEventDetail>).detail
    );
  };
  window.addEventListener("copilot-self-help-journey-event", listener);
  return () =>
    window.removeEventListener("copilot-self-help-journey-event", listener);
};

// Generated by Copilot
// updated on: 2026-09-14 | Story 1297509 | Added privacy-safe feedback-provided telemetry signal | Time Taken 2 min
// updated on: 2026-09-09 | Story 1297509 TASK-010 | Added centralized privacy-safe self-help telemetry adapter and listener | Time Taken 15 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Split form-start telemetry from submission-based completion with persistent dedupe | Time Taken 10 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Kept retryable submission failures non-terminal so later success completes once | Time Taken 3 min
// updated on: 2026-09-14 | 38 lines changed | [Auto Generated] | Add privacy-safe validator failure and failure-reason telemetry | Time Taken 5 min
// updated on: 2026-09-18 | 56 lines changed | [Auto Generated] | Emit authoritative AC-27 telemetry despite stale or abandoned UI snapshots | Time Taken 8 min
